import { useMemo } from 'react';
import { parseZonedDateTime } from '@internationalized/date';
import { MAX_LEG_FLIGHT_HOURS } from '@lp/features/RoutePlanner/config';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { getAircraftUtilizationWarningText } from '@lp/features/RoutePlanner/utils';
import {
  BaseSchemaValues,
  BaseTmdxAviaLegSchemaValues,
  CommonRouteLegSchemaValues,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { AircraftUtilizationParams, RouteLegTypeEnum } from '@/api/apis/route';
import { CaseStatus } from '@/api/apis/case';
import { useAircraftUtilizationQuery } from '@/queries/route-planner';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useWatchFields } from '@/hooks/useWatchFields';
import { isAllowedStatus } from '@/utils/helpers';

type LegFormUtilizationFields = Pick<BaseSchemaValues, 'legType'> &
  Pick<CommonRouteLegSchemaValues, 'departureDateTime'> &
  Pick<BaseTmdxAviaLegSchemaValues, 'selectedAircraftId'>;

export const useLegFormAircraftUtilizationWarnings = () => {
  const { caseId, status } = useBaseCaseDetails();
  const { Aircraft: aircrafts } = useRouteResources();
  const { legType, departureDateTime, selectedAircraftId } = useWatchFields<LegFormUtilizationFields>([
    'legType',
    'departureDateTime',
    'selectedAircraftId',
  ] as const);

  const debouncedDepartureDateTime = useDebouncedSearch(departureDateTime, 500);

  const isActiveCaseStatus = isAllowedStatus({
    statusFrom: CaseStatus.PendingLogistics,
    statusTo: CaseStatus.ReturningToBase,
    currentStatus: status,
  });

  const params = useMemo<AircraftUtilizationParams>(() => {
    if (!isActiveCaseStatus) return {};

    if (legType !== RouteLegTypeEnum.TmdxJetAviaLeg || !selectedAircraftId || !debouncedDepartureDateTime) {
      return {};
    }

    const departure = parseZonedDateTime(debouncedDepartureDateTime);

    return {
      aircraftIds: selectedAircraftId,
      arrivalTimeEqualOrGreaterThan: departure.toAbsoluteString(),
      departureTimeEqualOrLessThan: departure.add({ milliseconds: MAX_LEG_FLIGHT_HOURS }).toAbsoluteString(),
    };
  }, [legType, selectedAircraftId, debouncedDepartureDateTime, isActiveCaseStatus]);

  const { data } = useAircraftUtilizationQuery(params, !!params.aircraftIds);

  return useMemo(() => {
    if (!isActiveCaseStatus || !data?.length || !selectedAircraftId) return [];

    const tailNumber = aircrafts.find((a) => a.resourceId === selectedAircraftId)?.data?.registration;

    return data
      .filter((u) => u.caseId !== caseId && u.aircraftResource.id === selectedAircraftId)
      .map((u) => getAircraftUtilizationWarningText(u, tailNumber));
  }, [data, aircrafts, selectedAircraftId, caseId, isActiveCaseStatus]);
};
